import {
  Alert,
  Box,
  Button,
  Checkbox,
  CheckboxProps,
  Divider,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { RequestError, useApi } from "../../services";
import { BarragemData, BarragemProdutos, useUserContext } from "../../context";
import { AddBarragemModal, ErrorModal, ErrorOnDialog, ImagesBarragemModal, LoadingModal } from "../modals";
import { Add, AddPhotoAlternate, OpenInNew } from "@mui/icons-material";
import { useWindowSize } from "../../core";
import { useNavigate } from "react-router-dom";
import { CompanyData } from "../../pages";

export interface TableBarragemProps {
  title?: string;
  companyFilter?: CompanyData;
  isAddNew?: boolean;
  notEditable?: boolean;
  onSearch?: (barragem: BarragemData) => void;
}

export const TableBarragem: React.FC<TableBarragemProps> = ({ title, companyFilter, isAddNew, onSearch, notEditable }) => {
  const api = useApi();
  const user = useUserContext();
  const { height, width } = useWindowSize();

  const sizeRef = useRef<HTMLDivElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const timerFilter = useRef<NodeJS.Timer>();

  const [initialBarragem, setInitialBarragem] = useState<BarragemData>();
  const [state, setState] = useState(false);
  const [errorAdd, setErrorAdd] = useState<RequestError>();
  const [openNewModal, setOpenNewModal] = useState(false);
  const [barragens, setBrragens] = useState<BarragemData[] | RequestError>([]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // Lista de Barragens
  useEffect(() => {
    const getData = async () => {
      if (!user || "error" in user) return;

      const data = await api.barragem.getBarragens();
      setBrragens(data);
    };

    if (timerFilter.current) clearTimeout(timerFilter.current);

    timerFilter.current = setTimeout(() => {
      getData();
    }, 100);

  }, [user, state]);

  useEffect(() => {
    setTimeout(() => {
      let heightTable = height;
      let widthTable = width;
      if (sizeRef.current) {
        const size = sizeRef.current.getBoundingClientRect();
        widthTable = size.width;
        heightTable = size.height;
      }
      if (divRef.current) {
        const size = divRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (filterRef.current) {
        const size = filterRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      if (titleRef.current) {
        const size = titleRef.current.getBoundingClientRect();
        heightTable -= size.height;
      }
      setSize({ width: widthTable, height: heightTable });
    }, 100);

  }, [sizeRef, divRef, filterRef, titleRef, height, width]);

  const handleAdd = async (value?: BarragemData) => {
    if (!value) return;

    setOpenNewModal(false);
    setState(!state);
  };

  if ("error" in barragens) return <ErrorModal {...barragens} />;

  return <>
    {/* Erro */}
    {errorAdd &&
      <Snackbar
        open={Boolean(errorAdd)}
        autoHideDuration={4500}
        onClose={() => setErrorAdd(undefined)}
        message="Note archived"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setErrorAdd(undefined)} severity="error" sx={{ width: "100%" }}>
          {errorAdd.error + ":" + errorAdd.message}
        </Alert>
      </Snackbar>
    }

    {/* Modal para o nova barragem */}
    {openNewModal &&
      <AddBarragemModal
        open={openNewModal}
        onBarragem={handleAdd}
        onCancel={() => setOpenNewModal(false)}
      />
    }

    {/* Modal para as imagens da barragem */}
    {initialBarragem &&
      <ImagesBarragemModal
        open={Boolean(initialBarragem)}
        barragem={initialBarragem}
        onBarragem={handleAdd}
        onCancel={() => {
          setInitialBarragem(undefined);
          setState(!state);
        }}
      />
    }

    {/* Tabela com os dados */}
    <Box
      ref={sizeRef}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Título e nova barragem */}
      <Box
        ref={titleRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography fontWeight={"bold"} fontSize={24}>
          {title ?? "Barragens"}
        </Typography>

        {isAddNew && user && !("error" in user) && user.admin &&
          <Button
            variant="contained"
            onClick={() => setOpenNewModal(true)}
            endIcon={<Add />}
          >
            Nova Barragem
          </Button>
        }
      </Box>

      {/* Tabela com os contratos */}
      <TableContainer sx={{ flexGrow: 1, width: size.width, height: size.height, overflow: "auto" }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {onSearch && <TableCell align="center" sx={{ fontWeight: "bold" }}>Selecionar</TableCell>}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>Nome</TableCell>
              {!notEditable && <TableCell align="center" sx={{ fontWeight: "bold" }}>Produtos</TableCell>}
              <TableCell align="center" sx={{ fontWeight: "bold" }}>{(notEditable ? "" : "Editar ") + "Acessar"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {barragens.filter(barragem => companyFilter ? (barragem.idEmpreendimento === companyFilter.id) : true).map(barragem => <RowWaiting
              key={barragem.id}
              barragem={barragem}
              notEditable={notEditable}
              onEdit={() => setInitialBarragem(barragem)}
              onSearch={onSearch}
            />)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </>;
};

const RowWaiting: React.FC<{
  notEditable?: boolean;
  barragem: BarragemData;
  onEdit: (barragem: BarragemData) => void;
  onSearch?: (barragem: BarragemData) => void;
}> = ({ barragem, notEditable, onSearch, onEdit }) => {
  const navigate = useNavigate();
  const api = useApi();

  const [barragemData, setBarragemData] = useState(barragem);
  const [error, setError] = useState<RequestError>();
  const [loading, setLoading] = useState(false);


  const onChangeProduto = (produtos: BarragemProdutos) => {
    setLoading(true);
    api.barragem.updateProdutos(barragemData.id, produtos).then(val => {
      setLoading(false);
      if ("error" in val) {
        setError(val);
        return;
      }
      setBarragemData(val);
    });
  };

  return <>
    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    {loading && <LoadingModal open={loading} />}

    <TableRow hover>
      {onSearch && <TableCell align="center">
        <IconButton onClick={() => onSearch(barragemData)}>
          <OpenInNew />
        </IconButton>
      </TableCell>}

      {/* Nome */}
      <TableCell align="center">{barragemData.nome}</TableCell>

      {/* Produtos */}
      {!notEditable && <TableCell align="center" sx={{ width: "250px" }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px" }}>
          <ProdutoCheckbox
            title="PAEBM"
            checked={barragemData.produtos.pae}
            onChange={(_, checked) => onChangeProduto({ ...barragemData.produtos, pae: checked })}
          />

          <Divider orientation="vertical" flexItem />

          <ProdutoCheckbox
            title="MANCHAS"
            checked={barragemData.produtos.manchas}
            onChange={(_, checked) => onChangeProduto({ ...barragemData.produtos, manchas: checked })}
          />

          <Divider orientation="vertical" flexItem />

          <ProdutoCheckbox
            title="GISTM"
            checked={barragemData.produtos.gistm}
            onChange={(_, checked) => onChangeProduto({ ...barragemData.produtos, gistm: checked })}
          />
        </Box>

      </TableCell>}

      {/* Editar, Acessar */}
      <TableCell align="center" sx={{ width: "250px" }}>
        {!notEditable && <Tooltip title='Editar Imagens'>
          <IconButton size="small" onClick={() => onEdit(barragemData)}>
            <AddPhotoAlternate fontSize="small" />
          </IconButton>
        </Tooltip>}

        <Tooltip title='Ir para a barragem'>
          <IconButton size="small" onClick={() => navigate(`/app/${barragemData.id}`)}>
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>

    </TableRow >
  </>;
};

interface ProdutoCheckboxProps extends Omit<CheckboxProps, "title"> {
  title: React.ReactNode;
}

const ProdutoCheckbox: React.FC<ProdutoCheckboxProps> = ({ title, ...checkProps }) => {
  return <Box sx={{ display: "flex", flexDirection: 'column', alignItems: "center" }}>
    <Typography fontSize={10} fontWeight={"bold"}>{title}</Typography>
    <Box sx={{ width: "45px", height: "45px" }}>
      <Checkbox {...checkProps} size="small" />
    </Box>
  </Box>;
};